import type { UtmKey, UtmValues } from '@/components/builder/UtmSection'
import type { PickEmission } from '@/lib/pickerBindings'
import type { TagEntry } from '@/lib/tagEntry'

interface SaveArgs {
  utm: UtmValues
  tags: ReadonlyArray<TagEntry>
  pickEmissions?: ReadonlyArray<PickEmission>
}

export interface PresetQueryParameter {
  tagKey: string
  tagValue: string | undefined
}

/**
 * Flattens builder state into preset queryParameters. Empty values become
 * undefined (the preset semantic for "include this field, user fills it
 * at link-creation time") rather than '' so the backend treats them as
 * placeholders rather than zero-width concrete values. A picked value saves
 * both of its facts — the name key's label and, right after it, the `_id`
 * twin's value — so applying the preset can render the field as picked.
 */
export function toPresetQueryParameters({
  utm,
  tags,
  pickEmissions = [],
}: SaveArgs): PresetQueryParameter[] {
  const emissionByNameKey = new Map(pickEmissions.map((e) => [e.nameKey, e]))
  const out: PresetQueryParameter[] = []
  for (const key of Object.keys(utm) as UtmKey[]) {
    out.push({ tagKey: `utm_${key}`, tagValue: utm[key] === '' ? undefined : utm[key] })
  }
  for (const tag of tags) {
    if (tag.key === '') continue
    out.push({ tagKey: tag.key, tagValue: tag.value === '' ? undefined : tag.value })
    const emission = emissionByNameKey.get(tag.key)
    if (emission) {
      out.push({ tagKey: emission.idKey, tagValue: emission.idValue })
      emissionByNameKey.delete(tag.key)
    }
  }
  return out
}

export function queryParametersEqual(
  a: ReadonlyArray<PresetQueryParameter>,
  b: ReadonlyArray<PresetQueryParameter>,
): boolean {
  if (a.length !== b.length) return false
  const sortedA = [...a].sort((x, y) => x.tagKey.localeCompare(y.tagKey))
  const sortedB = [...b].sort((x, y) => x.tagKey.localeCompare(y.tagKey))
  for (let i = 0; i < sortedA.length; i++) {
    const ea = sortedA[i]
    const eb = sortedB[i]
    if (!ea || !eb) return false
    if (ea.tagKey !== eb.tagKey) return false
    if ((ea.tagValue ?? '') !== (eb.tagValue ?? '')) return false
  }
  return true
}
